/**
 * Job Priorities API Contract
 *
 * ts-rest contract definition for the job priorities endpoint used by works-lite.
 * This contract ensures type safety between frontend and backend.
 *
 * Endpoint  : GET /jobs/job-priorities
 * Method    : GET
 * Auth      : Required (standard bearer token via middleware)
 * Response  : Array of distinct priority strings from active jobs, ordered A–Z
 *
 * Backend SQL:
 *   SELECT DISTINCT j.priority
 *   FROM ${TABLES.JOB} j
 *   WHERE j.is_delete = false
 *   ORDER BY j.priority ASC;
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Zod schema for a single priority row
 */
export const jobPrioritySchema = z.object({
  id: z.number(),
  name: z.string(),
  display_order: z.number().nullable(),
  description: z.string().nullable(),
});

/**
 * Zod schema for the priorities list response
 */
export const jobPrioritiesResponseSchema = z.array(jobPrioritySchema);

/**
 * TypeScript types inferred from Zod schemas
 */
export type JobPriority = z.infer<typeof jobPrioritySchema>;
export type JobPrioritiesResponse = z.infer<typeof jobPrioritiesResponseSchema>;

/**
 * Job Priorities API Contract
 *
 * Base path: /jobs/job-priorities  (mounted in apps/backend/src/routes/index.ts)
 */
export const jobPrioritiesContract = c.router({
  /**
   * Get all distinct priorities used by active jobs
   *
   * GET /jobs/job-priorities
   *
   * Returns every distinct priority value from skewb_works.job
   * where is_delete = false, sorted alphabetically.
   * No query parameters required.
   *
   * Used by: works-lite work orders priority filter dropdown.
   */
  getJobPriorities: {
    method: 'GET',
    path: '/',
    responses: {
      200: jobPrioritiesResponseSchema,
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Get all distinct job priorities ordered alphabetically',
    description:
      'Returns all distinct priority values from active jobs (is_delete = false), ordered A–Z.',
  },
});
